import { ArrowUpRight, Check } from "lucide-react";
import { Reveal } from "./Reveal";
import { scrollToId } from "@/lib/scroll";
import { getCompanies } from "@/data/content";
import { UI } from "@/i18n/copy";
import { useLang } from "@/i18n/LanguageContext";

const CompanySection = ({ company, index, enquirePrefix }) => {
  const flipped = index % 2 === 1;
  const Icon = company.icon;

  return (
    <section
      id={`company-${company.id}`}
      data-testid={`company-section-${company.id}`}
      className={`scroll-mt-24 border-t border-black/10 ${flipped ? "bg-zinc-50" : "bg-white"}`}
    >
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 py-24 sm:px-8 sm:py-28 lg:grid-cols-2 lg:gap-20">
        <Reveal className={flipped ? "lg:order-2" : ""}>
          <div className="relative">
            <div
              className="absolute inset-0 translate-x-4 translate-y-4 rounded-3xl rtl:-translate-x-4 rtl:translate-y-4"
              style={{ backgroundColor: company.color }}
              aria-hidden="true"
            />
            <div className="relative overflow-hidden rounded-3xl">
              <img
                src={company.image}
                alt={company.imageAlt}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover transition-transform duration-700 ease-out hover:scale-105"
              />
            </div>
            <div
              className="absolute -bottom-5 start-6 flex items-center gap-2 rounded-full px-5 py-2.5 font-mono-label text-[11px] uppercase tracking-[0.2em] text-white shadow-lg"
              style={{ backgroundColor: company.color }}
            >
              <Icon className="h-3.5 w-3.5" />
              {company.est}
            </div>
          </div>
        </Reveal>

        <div className={flipped ? "lg:order-1" : ""}>
          <Reveal>
            <p className="flex items-center gap-3 font-mono-label text-[11px] uppercase tracking-[0.3em] text-black/50">
              <span className="h-2 w-2 rotate-45" style={{ backgroundColor: company.color }} />
              {company.num} — {company.tagline}
            </p>
            <div className="mt-6">
              {company.logo ? (
                <img
                  src={company.logo}
                  alt={`${company.name} logo`}
                  data-testid={`company-logo-${company.id}`}
                  className={
                    company.logoDark
                      ? "block h-20 w-auto rounded-2xl bg-[#1a1a1a] p-3 object-contain sm:h-24"
                      : "block h-14 w-auto object-contain sm:h-16"
                  }
                />
              ) : (
                <h3 className="text-4xl font-black tracking-tight text-black sm:text-5xl">
                  {company.name}
                </h3>
              )}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mt-6 space-y-5">
              {company.paragraphs.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-black/60">
                  {p}
                </p>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className={`mt-8 grid gap-8 ${company.lists.length > 1 ? "sm:grid-cols-2" : ""}`}>
              {company.lists.map((list) => (
                <div key={list.title}>
                  <h4 className="text-sm font-bold uppercase tracking-[0.12em] text-black">
                    {list.title}
                  </h4>
                  <ul className="mt-4 space-y-3">
                    {list.items.map((item) => (
                      <li key={item} className="flex items-start gap-3 text-sm leading-relaxed text-black/65">
                        <span
                          className="mt-0.5 inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-white"
                          style={{ backgroundColor: company.color }}
                        >
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </span>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <button
              data-testid={`company-enquire-${company.id}`}
              onClick={() => scrollToId("contact")}
              className="group mt-10 inline-flex items-center gap-2 text-[13px] font-bold uppercase tracking-[0.16em] text-black"
            >
              <span
                className="border-b-2 pb-1 transition-colors duration-300 group-hover:text-black/60"
                style={{ borderColor: company.color }}
              >
                {enquirePrefix} {company.name}
              </span>
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 rtl:-scale-x-100" />
            </button>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const CompanySections = () => {
  const { lang } = useLang();
  const companies = getCompanies(lang);
  const enquirePrefix = UI[lang].companies.enquirePrefix;

  return (
    <>
      {companies.map((c, i) => (
        <CompanySection key={c.id} company={c} index={i} enquirePrefix={enquirePrefix} />
      ))}
    </>
  );
};
